import { useEffect, useRef } from "react";

/**
 * Safely run async operations in useEffect with automatic cleanup.
 * Provides an `isMounted` check to prevent state updates after unmount.
 *
 * @param {Function} asyncFn - Async function that receives { isMounted: () => boolean }
 * @param {Array} deps - Dependency array (same as useEffect)
 *
 * @example
 * useAsyncEffect(async ({ isMounted }) => {
 *   const data = await fetchData();
 *   if (isMounted()) {
 *     setState(data);
 *   }
 * }, [dependency]);
 */
export function useAsyncEffect(asyncFn, deps) {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    asyncFn({
      isMounted: () => isMountedRef.current,
    });

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
